import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, Form, Button, Table, Alert, Badge, InputGroup } from 'react-bootstrap';
import api from '../../../utils/api';

const todayStr = () => new Date().toLocaleDateString('en-CA');

const TodayTab = () => {
  const [date, setDate] = useState(todayStr());
  const [log, setLog] = useState(null);
  const [settings, setSettings] = useState(null);
  const [oura, setOura] = useState(null);
  const [status, setStatus] = useState(null);
  const [customDrink, setCustomDrink] = useState({ name: '', oz: '', calories: '' });

  const load = useCallback(async (d) => {
    setStatus(null);
    try {
      const [logRes, settingsRes, trendsRes] = await Promise.all([
        api.get(`/health/daily/${d}`),
        api.get('/health/settings'),
        api.get('/health/trends?days=1'),
      ]);
      setLog(logRes.data);
      setSettings(settingsRes.data);
      setOura(trendsRes.data.oura.find((o) => o.date === d) || null);
    } catch (err) {
      setStatus({ type: 'danger', msg: 'Failed to load daily log.' });
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const save = async (updates) => {
    try {
      const { data } = await api.put(`/health/daily/${date}`, updates);
      setLog(data);
      setStatus({ type: 'success', msg: 'Saved.' });
      setTimeout(() => setStatus(null), 2000);
    } catch (err) {
      setStatus({ type: 'danger', msg: 'Save failed.' });
    }
  };

  const toggleMed = (idx) => {
    const meds = log.meds.map((m, i) => (i === idx ? { ...m, taken: !m.taken } : m));
    setLog({ ...log, meds });
    save({ meds });
  };

  const addDrink = (drink) => {
    const drinks = [...(log.drinks || []), { ...drink, time: new Date().toISOString() }];
    setLog({ ...log, drinks });
    save({ drinks });
  };

  const removeDrink = (idx) => {
    const drinks = log.drinks.filter((_, i) => i !== idx);
    setLog({ ...log, drinks });
    save({ drinks });
  };

  const addCustomDrink = () => {
    if (!customDrink.name) return;
    addDrink({
      name: customDrink.name,
      type: 'other',
      oz: Number(customDrink.oz) || 0,
      calories: Number(customDrink.calories) || 0,
    });
    setCustomDrink({ name: '', oz: '', calories: '' });
  };

  if (!log) return <p>Loading...</p>;

  const drinkCals = (log.drinks || []).reduce((s, d) => s + (d.calories || 0), 0);

  return (
    <div>
      <Row className="align-items-end mb-3">
        <Col xs="auto">
          <Form.Group>
            <Form.Label>Date</Form.Label>
            <Form.Control type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)} />
          </Form.Group>
        </Col>
        <Col>{status && <Alert variant={status.type} className="mb-0 py-2">{status.msg}</Alert>}</Col>
      </Row>

      {oura && (
        <Card className="mb-3">
          <Card.Body className="py-2">
            <strong>Oura:</strong>{' '}
            {oura.sleepScore != null && <Badge bg="secondary" className="me-2">Sleep {oura.sleepScore}</Badge>}
            {oura.readinessScore != null && <Badge bg="secondary" className="me-2">Readiness {oura.readinessScore}</Badge>}
            {oura.activityScore != null && <Badge bg="secondary" className="me-2">Activity {oura.activityScore}</Badge>}
            {oura.steps != null && <Badge bg="secondary" className="me-2">{oura.steps.toLocaleString()} steps</Badge>}
            {oura.totalSleepHours != null && <Badge bg="secondary">{oura.totalSleepHours} hrs sleep</Badge>}
          </Card.Body>
        </Card>
      )}

      <Row>
        <Col md={5}>
          <Card className="mb-3">
            <Card.Header>Medications</Card.Header>
            <Card.Body>
              {log.meds && log.meds.length > 0 ? (
                log.meds.map((m, i) => (
                  <Form.Check
                    key={i}
                    type="checkbox"
                    id={`med-${i}`}
                    label={m.name}
                    checked={m.taken}
                    onChange={() => toggleMed(i)}
                    className="mb-2"
                  />
                ))
              ) : (
                <p className="text-muted mb-0">No medications set up. Add them in Settings.</p>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Weight & Notes</Card.Header>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Weight (lbs)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={log.weight ?? ''}
                  onChange={(e) => setLog({ ...log, weight: e.target.value })}
                  onBlur={() => save({ weight: log.weight === '' ? null : Number(log.weight) })}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={log.notes || ''}
                  onChange={(e) => setLog({ ...log, notes: e.target.value })}
                  onBlur={() => save({ notes: log.notes })}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col md={7}>
          <Card className="mb-3">
            <Card.Header>
              Alcohol — {(log.drinks || []).length} drink{(log.drinks || []).length === 1 ? '' : 's'}
              {drinkCals > 0 && <span className="text-muted"> ({drinkCals} cal)</span>}
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                {(settings?.drinkPresets || []).map((p, i) => (
                  <Button
                    key={i}
                    variant="outline-secondary"
                    size="sm"
                    className="me-2 mb-2"
                    onClick={() => addDrink({ name: p.name, type: p.type, oz: p.oz, calories: p.calories })}
                  >
                    + {p.name}
                  </Button>
                ))}
                {(settings?.drinkPresets || []).length === 0 && (
                  <p className="text-muted">No drink presets. Add them in Settings, or log a custom drink below.</p>
                )}
              </div>
              <InputGroup className="mb-3" size="sm">
                <Form.Control
                  placeholder="Custom drink"
                  value={customDrink.name}
                  onChange={(e) => setCustomDrink({ ...customDrink, name: e.target.value })}
                />
                <Form.Control
                  placeholder="oz"
                  type="number"
                  style={{ maxWidth: 80 }}
                  value={customDrink.oz}
                  onChange={(e) => setCustomDrink({ ...customDrink, oz: e.target.value })}
                />
                <Form.Control
                  placeholder="cal"
                  type="number"
                  style={{ maxWidth: 90 }}
                  value={customDrink.calories}
                  onChange={(e) => setCustomDrink({ ...customDrink, calories: e.target.value })}
                />
                <Button variant="outline-primary" onClick={addCustomDrink}>Add</Button>
              </InputGroup>
              {(log.drinks || []).length > 0 && (
                <Table size="sm" className="mb-0">
                  <tbody>
                    {log.drinks.map((d, i) => (
                      <tr key={i}>
                        <td>{d.name}</td>
                        <td className="text-muted">{d.oz ? `${d.oz} oz` : ''}</td>
                        <td className="text-muted">{d.calories ? `${d.calories} cal` : ''}</td>
                        <td className="text-end">
                          <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeDrink(i)}>
                            remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TodayTab;
